import {openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isString} from 'ramda-adjunct';

import {
  ConvertForAllVehiclesApiArg,
  UpdateInterestApiArg,
  useConvertForAllVehiclesMutation,
  useUpdateInterestMutation,
} from '@dms/api/finance';
import {ApiError} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {interestRoutes, testIds} from '@dms/routes';
import {
  DmsDataGrid,
  handleApiError,
  InterestEndCaseDialog,
  PersonnelOverview,
  PersonnelOverviewWrapper,
  queryParams,
  useMandatoryFields,
  useMandatoryFieldsErrorDialog,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const INTEREST_PERSONNEL_OVERVIEW_FILTER_ID = 'interest-personnel-overview-filter';
const GRID_CODE = 'interest';

interface SalesInterestOverviewProps {
  isPersonnelPanelOpen: boolean;
  canViewInterest: boolean;
}

export function SalesInterestOverview(props: SalesInterestOverviewProps) {
  const navigate = useNavigate();

  const [updateInterest] = useUpdateInterestMutation();
  const [convertForAllVehicles] = useConvertForAllVehiclesMutation();

  const {isMandatoryFieldsEnabled} = useMandatoryFields();
  const {handleMandatoryFieldsError, isMandatoryFieldsError} = useMandatoryFieldsErrorDialog();

  const actionCallback: ActionCallback = ({actionKey, rowId, sourceSearchParams, refreshData}) => {
    if (!isString(rowId)) {
      showNotification.error();
      return;
    }

    match(actionKey)
      .with('detail', () =>
        navigate(
          composePath(interestRoutes.detailOverview, {
            params: {id: rowId},
            isSearchParamsPreserved: false,
            queryParams: `${sourceSearchParams}&${queryParams.REDIRECT_SOURCE}=interest`,
          })
        )
      )
      .with('detail_newTab', () => {
        window.open(
          composePath(interestRoutes.detailOverview, {
            params: {id: rowId},
            isSearchParamsPreserved: false,
            queryParams: `${sourceSearchParams}&${queryParams.REDIRECT_SOURCE}=interest`,
          })
        );
      })
      .with('open', () => {
        const args: UpdateInterestApiArg = {
          id: rowId,
          updateInterestRequestBody: {
            state: 'OPEN',
          },
        };
        updateInterest(args)
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('end', () =>
        openDialog(
          <InterestEndCaseDialog
            data-testid={testIds.interest.overview('endCaseDialog')}
            interestId={rowId}
            onSubmit={refreshData}
          />,
          {
            'data-testid': testIds.interest.overview('endCaseDialog'),
            title: i18n.t('entity.interest.labels.endInterestCase'),
            scrollBehavior: 'outside',
          }
        )
      )
      .with('createBusinessCase', () => {
        const args: ConvertForAllVehiclesApiArg = {
          interestId: rowId ?? '',
          validateMandatoryFields: isMandatoryFieldsEnabled(undefined),
        };
        convertForAllVehicles(args)
          .unwrap()
          .then(() => {
            refreshData();
          })
          .catch((error: ApiError) => {
            if (isMandatoryFieldsError(error)) {
              return handleMandatoryFieldsError(error.data?.validationErrors ?? []);
            }

            return handleApiError(error);
          });
      })
      .otherwise(() => {
        showNotification.warning(`Action callback was not specified for action ${actionKey}`);
      });
  };

  const isPersonnelOverviewOpen = props.isPersonnelPanelOpen && props.canViewInterest;

  return (
    <PersonnelOverviewWrapper
      externalFilterId={isPersonnelOverviewOpen ? INTEREST_PERSONNEL_OVERVIEW_FILTER_ID : undefined}
      data-testid={testIds.interest.overview('personnelOverviewWrapper')}
    >
      <DmsDataGrid
        data-testid={testIds.interest.overview('dataGrid')}
        gridCode={GRID_CODE}
        actionCallback={actionCallback}
        externalFilterId={INTEREST_PERSONNEL_OVERVIEW_FILTER_ID}
        filterComponent={(connectorProps) => (
          <PersonnelOverview
            entity={GRID_CODE}
            isOpen={isPersonnelOverviewOpen}
            values={connectorProps.values}
            onChange={connectorProps.onChange}
            data-testid={testIds.interest.overview('personnelOverview')}
          />
        )}
      />
    </PersonnelOverviewWrapper>
  );
}
